import TicketStack from 'components/dashboard/entries-and-stats/TicketStack';
import { PromoCodeEntry } from 'components/enter/order-summary/PromoCodeEntry';
import EnterLayout from 'components/enter2026/EnterLayout';
import OrderSummaryCosts from 'components/enter2026/OrderSummaryCosts';
import OrderSummaryRows from 'components/enter2026/OrderSummaryRows';
import V1CheckoutAndIdentity from 'components/enter2026late/V1/V1CheckoutAndIdentity';
import V2CheckoutAndIdentity from 'components/enter2026late/V2/V2CheckoutAndIdentity';
import MainLayout from 'components/layout/MainLayout';
import { PaymentCheckoutWrapper } from 'components/payments/PaymentCheckoutWrapper';
import { useEnterFlowPreconditionCheck } from 'hooks/enter/useEnterFlowPreconditionCheck';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps } from 'next';
import { useEffect, useState } from 'react';
import { getCheckout2026Value } from 'utils/ab-test/abTests';
import { trackCheckoutPageViewed } from 'utils/analytics/analytics';
import { ASSET_PREFIX } from 'utils/constants';
import ShoppingCart from '../components/svg/ShoppingCart.svg';
import styles from './checkout.module.css';

interface EnterNowCheckoutProps {}

const ControlEnterNowCheckout = observer((props: EnterNowCheckoutProps) => {
  const enterStore = useEnterStore();
  const userStore = useUserStore();

  useEffect(() => {
    if (
      enterStore.initialised &&
      userStore.userInitialized &&
      userStore.currentUser
    ) {
      trackCheckoutPageViewed({
        numberOfLines: enterStore.lines.length,
        valueInPence: enterStore.orderCost.cost,
        userId: userStore.currentUser.id,
        rawEmailAddress: userStore.currentUser.emailAddress,
      });
    }
  }, [enterStore.initialised, userStore.userInitialized]);

  return (
    <MainLayout limitItemWidths>
      <EnterLayout
        sidePanel={
          <div className={styles.sidePanel}>
            <h2 className={'aboutUsTypographyHeaderMedium ' + styles.heading}>
              <ShoppingCart />
              <span>Your order</span>
            </h2>
            <hr />
            <p>
              Your numbers look great!
              <br />
              Here they are once more
            </p>
            <div className={styles.entryContainer}>
              <TicketStack
                entries={enterStore.lines}
                withLoadingSkeleton={false}
                expanded
                hideExpandButton
              />
            </div>
            <hr />
            <OrderSummaryRows showDays={true} />
            <hr />
            <PromoCodeEntry />
            <OrderSummaryCosts />
            <strong className={styles.cancelText}>👋 Cancel anytime 👋</strong>
          </div>
        }
        sidePanelBackground={'GREY'}
        stepIcon={null}
        stepIntroText={null}
        stepName="Payment"
        stepIndex={5}
      >
        <div className={styles.mainPanel}>
          <PaymentCheckoutWrapper />
        </div>
        <div className={styles.logos}>
          <img
            src={`${ASSET_PREFIX}/images/enter/payment-icons/secure-checkout.svg`}
            alt=""
          />
          <img
            src={`${ASSET_PREFIX}/images/enter/payment-icons/privacy-protected.svg`}
            alt=""
          />
          <img
            src={`${ASSET_PREFIX}/images/enter/payment-icons/trustpilot.svg`}
            alt=""
          />
        </div>
      </EnterLayout>
    </MainLayout>
  );
});

const EnterNowCheckoutWrapper = () => {
  const [checkoutVariant, setCheckoutVariant] = useState<
    ReturnType<typeof getCheckout2026Value> | undefined
  >();
  useEnterFlowPreconditionCheck();
  useEffect(() => {
    setCheckoutVariant(getCheckout2026Value());
  }, []);

  if (!checkoutVariant) {
    // The CONTROL variant performs a bunch of validation effects, so we have to render a blank page until we definitely
    // know what variant we're dealing with
    return <></>;
  }

  if (checkoutVariant === 'V1') {
    return <V1CheckoutAndIdentity />;
  }
  if (checkoutVariant === 'V2') {
    return <V2CheckoutAndIdentity />;
  }

  return <ControlEnterNowCheckout />;
};

export default EnterNowCheckoutWrapper;

export const getServerSideProps: GetServerSideProps<
  EnterNowCheckoutProps
> = async (_ctx) => {
  return {
    props: {},
  };
};
